import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetShipmentContainersListViaTaskIdMutation,
  useGetShipmentContainerMediaByPhotoTypeViaTaskIdMutation,
  useLazyGetSingleShipmentContainerMediaQuery,
} from "@/src/store/apis/shipments/shipmentDetail";
import {
  useContainerIdsFullBatchDownloadMutation,
  useTaskIdWithMediaTypeFullBatchDownloadMutation,
} from "@/src/store/apis/containerImage";
import { ContainerMediaType } from "@/src/store/apis/tasks/taskDetail/interface";
import { CONTAINER_VALUE_TYPE_CONTAINER_LABEL_TYPE_MAP } from "@/src/pages/exporter/Shipment/components/dialog/imageMapper";
import {
  ContainerPhotosAdapter,
  ContainerListItem,
  ContainerMediaData,
  MediaByPhotoType,
} from "../types";
import {
  mapContainerMediaResponse,
  mapMediaByPhotoTypeResponse,
} from "./utils/mapContainerMediaResponse";

/**
 * Exporter Task 모드용 Adapter
 * - 선적 등록 페이지에서 여러 Task를 선택하여 컨테이너 사진을 조회할 때 사용
 * - taskIds 배열을 기반으로 컨테이너 목록 조회
 * - 아직 선적에 연결되지 않은 컨테이너만 표시
 *
 * @param taskIds - 조회할 Task ID 배열
 * @returns ContainerPhotosAdapter - 통합 어댑터 인터페이스
 *
 * @example
 * const adapter = useExporterTaskAdapter([taskId1, taskId2]);
 * await adapter.fetchContainerList();
 * await adapter.fetchContainerMedia(containerId);
 */
export const useExporterTaskAdapter = (
  taskIds: number[]
): ContainerPhotosAdapter => {
  const { t } = useTranslation();

  const [containerList, setContainerList] = useState<ContainerListItem[]>();
  const [containerMedia, setContainerMedia] = useState<ContainerMediaData>();
  const [mediaByPhotoType, setMediaByPhotoType] = useState<MediaByPhotoType>();

  // Task 기반 조회는 Mutation을 사용 (POST로 taskIdList 전달)
  const [getContainerListByTaskIds, containerListResult] =
    useGetShipmentContainersListViaTaskIdMutation();
  const [getMediaByPhotoTypeByTaskIds, mediaByPhotoTypeResult] =
    useGetShipmentContainerMediaByPhotoTypeViaTaskIdMutation();

  // 단일 컨테이너 조회는 기존 Query 사용
  const [getSingleContainerMedia, singleContainerMediaResult] =
    useLazyGetSingleShipmentContainerMediaQuery();

  // 다운로드
  const [downloadByContainers, containerDownloadResult] =
    useContainerIdsFullBatchDownloadMutation();
  const [downloadByMediaTypes, photoTypeDownloadResult] =
    useTaskIdWithMediaTypeFullBatchDownloadMutation();

  const fetchContainerList = useCallback(async () => {
    if (taskIds.length === 0) return undefined;

    try {
      const response = await getContainerListByTaskIds({ taskIdList: taskIds }).unwrap();
      const mapped: ContainerListItem[] =
        response?.map((item) => ({
          id: item.id,
          containerNo: item.containerNo ?? null,
        })) ?? [];

      setContainerList(mapped);
      return mapped;
    } catch (error) {
      console.error("[useExporterTaskAdapter] fetchContainerList error:", error);
      return undefined;
    }
  }, [taskIds, getContainerListByTaskIds]);

  const fetchContainerMedia = useCallback(
    async (containerId: number) => {
      try {
        const response = await getSingleContainerMedia({
          id: containerId,
          mode: 'task',
          taskIds: taskIds.join(','),
        }).unwrap();
        if (response) {
          const mapped = mapContainerMediaResponse(response);
          setContainerMedia(mapped);
        }
      } catch (error) {
        console.error("[useExporterTaskAdapter] fetchContainerMedia error:", error);
      }
    },
    [getSingleContainerMedia, taskIds]
  );

  const fetchMediaByPhotoType = useCallback(async () => {
    if (taskIds.length === 0) return;

    try {
      const response = await getMediaByPhotoTypeByTaskIds({ taskIdList: taskIds }).unwrap();
      if (response) {
        const mapped = mapMediaByPhotoTypeResponse(response);
        setMediaByPhotoType(mapped);
      }
    } catch (error) {
      console.error("[useExporterTaskAdapter] fetchMediaByPhotoType error:", error);
    }
  }, [taskIds, getMediaByPhotoTypeByTaskIds]);

  const downloadByContainerIds = useCallback(
    async (ids: number[]) => {
      if (ids.length === 0) return;
      try {
        await downloadByContainers({ containerIdList: ids }).unwrap();
      } catch (error) {
        console.error("[useExporterTaskAdapter] downloadByContainerIds error:", error);
      }
    },
    [downloadByContainers]
  );

  const downloadByPhotoTypes = useCallback(
    async (types: ContainerMediaType[]) => {
      if (types.length === 0 || taskIds.length === 0) return;

      const photoTypeRequestIds = types
        .filter((type) => type.startsWith("CUSTOM_"))
        .map((type) => parseInt(type.replace("CUSTOM_", ""), 10))
        .filter((id) => !isNaN(id));

      const normalizedTypes = types
        .map((type) => (type.startsWith("CUSTOM_") ? "CUSTOM" : type))
        .filter((type, index, self) => self.indexOf(type) === index) as ContainerMediaType[];

      try {
        await downloadByMediaTypes({
          taskIdList: taskIds,
          containerMediaTypeList: normalizedTypes,
          photoTypeRequestIds: photoTypeRequestIds.length > 0 ? photoTypeRequestIds : undefined,
        }).unwrap();
      } catch (error) {
        console.error("[useExporterTaskAdapter] downloadByPhotoTypes error:", error);
      }
    },
    [taskIds, downloadByMediaTypes]
  );

  const getPhotoTypeLabel = useCallback(
    (type: ContainerMediaType): string => {
      const labelMap = CONTAINER_VALUE_TYPE_CONTAINER_LABEL_TYPE_MAP(t);
      return labelMap[type] || type;
    },
    [t]
  );

  return {
    containerList,
    containerMedia,
    mediaByPhotoType,

    isContainerListLoading: containerListResult.isLoading,
    isContainerMediaLoading: singleContainerMediaResult.isFetching,
    isMediaByPhotoTypeLoading: mediaByPhotoTypeResult.isLoading,

    fetchContainerList,
    fetchContainerMedia,
    fetchMediaByPhotoType,

    downloadByContainerIds,
    downloadByPhotoTypes,
    isContainerDownloading: containerDownloadResult.isLoading,
    isPhotoTypeDownloading: photoTypeDownloadResult.isLoading,

    getPhotoTypeLabel,
  };
};
